/**
 * Room Tool - Room Creation
 *
 * Places a Room element at a point.
 * Features:
 * - Point placement
 * - Automatic boundary detection from enclosing walls
 * - Tag on placement
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { collectWallLines, computeRoomBoundary, polygonArea } from '../utils/roomBoundary';
import i18n from '../i18n';

export interface RoomGeometryData {
    position: [number, number, number];
    levelId: string;
    boundary?: [number, number][]; // Calculated boundary
}

export interface RoomElement extends Element {
    category: 'Rooms';
    geometryData: RoomGeometryData;
}

export class RoomTool extends BaseTool {
    id = 'room';
    name = 'Room';
    cursor = 'crosshair';

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);
        this.createRoom(snapped);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            const snapped = this.snapToGrid(point);
            this.context.appStore.setToolPreview({
                type: 'room',
                position: snapped
            });
        }
    }

    onPointerUp(): void { }

    private createRoom(point: THREE.Vector3) {
        if (!this.context) return;
        const levelId = 'Level 1'; // Should be active level

        // Automatic boundary detection: clip the wall faces enclosing the
        // click point. Fall back to a minimal marker boundary when the point
        // sits in open space so the room stays selectable (BIM shows a
        // room with a red crosshair when its boundary is not closed).
        const walls = collectWallLines(this.context.bimStore.project.elements);
        const detected = computeRoomBoundary(point, walls);
        const boundary: [number, number][] = detected
            ? detected.boundary
            : [
                [point.x - 0.5, point.y - 0.5],
                [point.x + 0.5, point.y - 0.5],
                [point.x + 0.5, point.y + 0.5],
                [point.x - 0.5, point.y + 0.5],
            ];
        const areaM2 = polygonArea(boundary);
        if (!detected) {
            this.notify(i18n.t('tools.notify.roomBoundaryIsNotEnclosedBy'), 'warning');
        }

        const geometryData: RoomGeometryData = {
            position: [point.x, point.y, point.z],
            levelId,
            boundary // Auto-detected boundary
        };

        // Sequential room number instead of a random one (BIM numbering).
        const roomNumber = this.nextRoomNumber();

        const room: RoomElement = {
            id: uuidv4(),
            category: 'Rooms',
            type: 'Room',
            name: 'Room ' + roomNumber,
            parameters: {
                'Level': levelId,
                'Number': roomNumber,
                'Name': 'Room',
                'Area': areaM2.toFixed(2) + ' m²',
                'Perimeter': detected ? 'Auto' : 'Unclosed',
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(room);

        // Auto-Tag
        this.addAutoTag(room);
    }

    private nextRoomNumber(): string {
        if (!this.context) return '101';
        let max = 100;
        this.context.bimStore.project.elements.forEach((el) => {
            if (el.category !== 'Rooms') return;
            const n = parseInt(String(el.parameters['Number']), 10);
            if (Number.isFinite(n)) max = Math.max(max, n);
        });
        return String(max + 1);
    }

    private addAutoTag(room: RoomElement) {
        if (!this.context) return;
        const tagId = uuidv4();
        // Use 'any' to bypass strict Element check if TagElement is not imported
        const tag: any = {
            id: tagId,
            category: 'Tags',
            type: 'Room Tag',
            parameters: {
                'Target Id': room.id,
                'Value': room.parameters['Number'] || '101',
                'Parameter': 'Number'
            },
            constraints: [],
            geometryData: {
                position: room.geometryData.position,
                targetId: room.id
            }
        };
        this.context.bimStore.addElement(tag);
    }
}
